"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Image from "next/image";
import classNames from "classnames";
import Skeleton from "./Skeleton";
const FALLBACK_SRC = "/images/no-photo.png";
const FALLBACK_ALT = "No image available";

/**
 * `fill` next/image for an already-sized, position:relative container,
 * with a placeholder layer shown until the real image has loaded.
 *
 * Props:
 * - `src`, `alt` — passed straight to next/image.
 * - `className` — applied to the real `<Image>` (not the placeholder).
 * - `sizes`, `priority` — passed straight to next/image.
 * - `blurDataURL` — optional per-image low-res preview (e.g. a base64
 *   data URL). When provided, it's shown as a static blurred background.
 *   When omitted, a generic shimmer `Skeleton` is shown instead.
 *
 * - `natural` — render at the image's own aspect ratio (width 100%, height
 *   auto) in a non-sized container, instead of filling it. For masonry tiles.
 *
 * A missing `src`, or one that fails to load, swaps to `no-photo.png` with
 * alt "No image available" (the `alt` attribute is always kept).
 */
export default function ProgressiveImage(t0) {
  const $ = _c(33);
  let alt;
  let blurDataURL;
  let props;
  let sizes;
  let src;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      src,
      alt,
      className: t1,
      sizes,
      priority: t2,
      blurDataURL,
      natural: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = blurDataURL;
    $[3] = props;
    $[4] = sizes;
    $[5] = src;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    alt = $[1];
    blurDataURL = $[2];
    props = $[3];
    sizes = $[4];
    src = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
  }
  const className = t1 === undefined ? "" : t1;
  const priority = t2 === undefined ? false : t2;
  const natural = t3 === undefined ? false : t3;
  const [isLoaded, setIsLoaded] = useState(false);
  const [failedSrc, setFailedSrc] = useState(null);
  const useFallback = !src || failedSrc === src;
  let t4;
  if ($[9] !== blurDataURL || $[10] !== isLoaded) {
    t4 = blurDataURL ? <div aria-hidden="true" className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: `url(${blurDataURL})`
    }} /> : <Skeleton width="100%" height="100%" rounded="rounded-none" className={classNames("absolute inset-0", isLoaded && "!animate-none")} />;
    $[9] = blurDataURL;
    $[10] = isLoaded;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = useFallback ? FALLBACK_SRC : src;
  const t6 = useFallback ? FALLBACK_ALT : alt;
  let t7;
  if ($[12] !== natural) {
    t7 = natural ? {
      width: 800,
      height: 800
    } : {
      fill: true
    };
    $[12] = natural;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsLoaded(true);
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== src) {
    t9 = () => setFailedSrc(src);
    $[15] = src;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = natural && "relative h-auto w-full";
  const t11 = isLoaded ? "opacity-100" : "opacity-0";
  let t12;
  if ($[17] !== className || $[18] !== t10 || $[19] !== t11) {
    t12 = classNames("transition-opacity duration-300", t10, t11, className);
    $[17] = className;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== priority || $[22] !== props || $[23] !== sizes || $[24] !== t12 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t9) {
    t13 = <Image src={t5} alt={t6} {...t7} sizes={sizes} priority={priority} onLoad={t8} onError={t9} className={t12} {...props} />;
    $[21] = priority;
    $[22] = props;
    $[23] = sizes;
    $[24] = t12;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13 || $[31] !== t4) {
    t14 = <>{t4}{t13}</>;
    $[30] = t13;
    $[31] = t4;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}